/**
 * A saved rule tree, read-only: what a rule segment's page shows above its
 * Edit button, and what a Flow condition or a flow's audience can show beside a
 * segment's name.
 *
 * Plain sentences, no JSON. Each leaf is `ruleLeafSummary` — "Age ≥ 25",
 * "City any of Pune, Nashik" — over the catalogue's label for the key, so a
 * column a person renamed reads with its new name; a key the catalogue no
 * longer has prints the key itself in a "Missing" chip rather than vanishing,
 * because the saved tree still holds it and the compiler will say so. Groups
 * keep their ALL / ANY word and nest as lists, so a screen reader hears the
 * structure (`ul` inside `li`) rather than a run of sentences.
 */
import { ruleLeafSummary, type Field, type RuleGroup, type RuleNode } from '@wend/shared';
import { Tag, cn } from '@wend/ui';

import { FIELD_GROUP_TAG, FIELD_GROUP_TAG_TONE } from './FieldPicker';

export interface RuleTreeSummaryProps {
  readonly tree: RuleGroup;
  readonly fields: readonly Field[];
  readonly className?: string;
}

function Node({ node, byKey, depth }: { node: RuleNode; byKey: ReadonlyMap<string, Field>; depth: number }) {
  if (node.kind === 'group') {
    return (
      <div className={cn(depth > 0 && 'rounded-sm border border-line px-3 py-2', depth > 0 && node.join === 'or' && 'border-warn/35')}>
        <p className="mb-1.5 text-[0.8rem] text-muted">
          {depth === 0 ? 'People who match ' : 'A group matching '}
          <span className={cn('font-mono text-[0.72rem] tracking-[0.08em]', node.join === 'and' ? 'text-brand-ink' : 'text-warn')}>
            {node.join === 'and' ? 'ALL' : 'ANY'}
          </span>{' '}
          of:
        </p>
        <ul className="flex flex-col gap-1.5">
          {node.children.map((child) => (
            <li key={child.id} className="min-w-0">
              <Node node={child} byKey={byKey} depth={depth + 1} />
            </li>
          ))}
        </ul>
      </div>
    );
  }
  const field = byKey.get(node.field);
  return (
    <span className="inline-flex flex-wrap items-center gap-2 text-[0.88rem] text-text" data-rule-id={node.id}>
      <Tag tone={field === undefined ? 'neutral' : FIELD_GROUP_TAG_TONE[field.group]}>
        {field === undefined ? 'Missing' : FIELD_GROUP_TAG[field.group]}
      </Tag>
      <span>{ruleLeafSummary(node, field === undefined ? null : { label: field.label, type: field.type })}</span>
    </span>
  );
}

export function RuleTreeSummary({ tree, fields, className }: RuleTreeSummaryProps) {
  const byKey = new Map(fields.map((field) => [field.key, field]));
  return (
    <div className={className} data-role="rule-summary">
      <Node node={tree} byKey={byKey} depth={0} />
    </div>
  );
}
